import { useState, useCallback } from 'react';
import { betaAPI } from '../../services/api';

/**
 * useBetaAdmin - Beta Feedback system state + handlers.
 */
export const useBetaAdmin = () => {
  const [betaFeedback, setBetaFeedback] = useState([]);
  const [betaStats, setBetaStats] = useState({
    testers: 0, total_feedback: 0, by_category: {}, by_status: {}, avg_resolution_hours: null,
  });
  const [betaTrends, setBetaTrends] = useState([]);
  const [betaTesters, setBetaTesters] = useState([]);
  const [betaLoading, setBetaLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDeleteFeedbackConfirm, setShowDeleteFeedbackConfirm] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [betaStatusFilter, setBetaStatusFilter] = useState('all');
  const [betaCategoryFilter, setBetaCategoryFilter] = useState('all');
  const [trendPeriod, setTrendPeriod] = useState(30);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [showCreateCodeModal, setShowCreateCodeModal] = useState(false);
  const [showCreateTesterModal, setShowCreateTesterModal] = useState(false);
  const [newCodeData, setNewCodeData] = useState({ code: '', max_uses: 1, expires_at: '' });
  const [betaSystemEnabled, setBetaSystemEnabled] = useState(true);
  const [adminResponseInput, setAdminResponseInput] = useState({});
  const [respondingTo, setRespondingTo] = useState(null);
  const [newTesterData, setNewTesterData] = useState({ name: '', email: '' });
  const [codeLoading, setCodeLoading] = useState(false);
  const [testerLoading, setTesterLoading] = useState(false);

  const fetchBetaData = useCallback(async () => {
    setBetaLoading(true);
    try {
      const [feedbackData, statsData, testersData, codesData, trendsData] = await Promise.all([
        betaAPI.getAllFeedback().catch(() => []),
        betaAPI.getStats().catch(() => ({ testers: 0, total_feedback: 0, by_category: {}, by_status: {}, avg_resolution_hours: null })),
        betaAPI.getTesters().catch(() => []),
        betaAPI.getInviteCodes().catch(() => []),
        betaAPI.getTrends(trendPeriod).catch(() => ({ trends: [] })),
      ]);
      setBetaFeedback(feedbackData);
      setBetaStats(statsData);
      setBetaTesters(testersData);
      setInviteCodes(codesData);
      setBetaTrends(trendsData.trends || []);
    } catch (error) {
      console.error('Failed to fetch beta data:', error);
    } finally {
      setBetaLoading(false);
    }
  }, [trendPeriod]);

  const fetchBetaSettings = useCallback(async () => {
    try {
      const settings = await betaAPI.getSettings();
      setBetaSystemEnabled(settings.is_active);
    } catch (error) {
      console.error('Failed to fetch beta settings:', error);
    }
  }, []);

  const handleToggleBetaSystem = useCallback(async () => {
    try {
      const result = await betaAPI.toggleBetaSystem();
      setBetaSystemEnabled(result.is_active);
    } catch (error) {
      console.error('Failed to toggle beta system:', error);
    }
  }, []);

  const handleAddAdminResponse = useCallback(async (feedbackId) => {
    const responseText = adminResponseInput[feedbackId];
    if (!responseText?.trim()) return;
    try {
      await betaAPI.addAdminResponse(feedbackId, responseText);
      setBetaFeedback((prev) => prev.map((f) => f.id === feedbackId
        ? { ...f, admin_response: responseText, admin_responded_at: new Date().toISOString() }
        : f
      ));
      setAdminResponseInput((prev) => ({ ...prev, [feedbackId]: '' }));
      setRespondingTo(null);
    } catch (error) {
      console.error('Failed to add admin response:', error);
    }
  }, [adminResponseInput]);

  const handleCreateCode = useCallback(async () => {
    setCodeLoading(true);
    try {
      const data = {
        code: newCodeData.code || null,
        max_uses: parseInt(newCodeData.max_uses) || 1,
        expires_at: newCodeData.expires_at || null,
      };
      const result = await betaAPI.createInviteCode(data);
      setInviteCodes((prev) => [{
        id: result.code, code: result.code, max_uses: result.max_uses,
        uses: 0, status: 'active', created_at: new Date().toISOString(),
      }, ...prev]);
      setShowCreateCodeModal(false);
      setNewCodeData({ code: '', max_uses: 1, expires_at: '' });
      fetchBetaData();
    } catch (error) {
      console.error('Failed to create code:', error);
      alert(error.response?.data?.detail || 'Fout bij aanmaken code');
    } finally {
      setCodeLoading(false);
    }
  }, [newCodeData, fetchBetaData]);

  const handleDeleteCode = useCallback(async (codeId) => {
    if (!window.confirm('Weet u zeker dat u deze code wilt verwijderen?')) return;
    try {
      await betaAPI.deleteInviteCode(codeId);
      setInviteCodes((prev) => prev.filter((c) => c.id !== codeId));
    } catch (error) {
      console.error('Failed to delete code:', error);
    }
  }, []);

  const handleToggleCode = useCallback(async (codeId) => {
    try {
      const result = await betaAPI.toggleInviteCode(codeId);
      setInviteCodes((prev) => prev.map((c) => (c.id === codeId
        ? { ...c, is_active: result.is_active, status: result.is_active ? 'active' : 'inactive' }
        : c
      )));
    } catch (error) {
      console.error('Failed to toggle code:', error);
    }
  }, []);

  const handleCreateTester = useCallback(async () => {
    setTesterLoading(true);
    try {
      const result = await betaAPI.createTester(newTesterData.name, newTesterData.email);
      setBetaTesters((prev) => [result.tester, ...prev]);
      setShowCreateTesterModal(false);
      setNewTesterData({ name: '', email: '' });
      fetchBetaData();
    } catch (error) {
      console.error('Failed to create tester:', error);
      alert(error.response?.data?.detail || 'Fout bij aanmaken tester');
    } finally {
      setTesterLoading(false);
    }
  }, [newTesterData, fetchBetaData]);

  const handleDeleteTester = useCallback(async (testerId) => {
    if (!window.confirm('Weet u zeker dat u deze beta-tester wilt verwijderen?')) return;
    try {
      await betaAPI.deleteTester(testerId);
      setBetaTesters((prev) => prev.filter((t) => t.id !== testerId));
      fetchBetaData();
    } catch (error) {
      console.error('Failed to delete tester:', error);
    }
  }, [fetchBetaData]);

  const handleToggleTester = useCallback(async (testerId) => {
    try {
      const result = await betaAPI.toggleTester(testerId);
      setBetaTesters((prev) => prev.map((t) => (t.id === testerId ? { ...t, is_active: result.is_active } : t)));
    } catch (error) {
      console.error('Failed to toggle tester:', error);
    }
  }, []);

  const handleDeleteFeedback = useCallback(async () => {
    if (!feedbackToDelete) return;
    try {
      await betaAPI.deleteFeedback(feedbackToDelete);
      setBetaFeedback((prev) => prev.filter((f) => f.id !== feedbackToDelete));
      setShowDeleteFeedbackConfirm(false);
      setFeedbackToDelete(null);
    } catch (error) {
      console.error('Failed to delete feedback:', error);
    }
  }, [feedbackToDelete]);

  const handleUpdateFeedbackStatus = useCallback(async (feedbackId, newStatus) => {
    try {
      await betaAPI.updateFeedbackStatus(feedbackId, newStatus);
      setBetaFeedback((prev) => prev.map((f) => (f.id === feedbackId ? { ...f, status: newStatus } : f)));
    } catch (error) {
      console.error('Failed to update feedback status:', error);
    }
  }, []);

  const handleExportJSON = useCallback(async () => {
    try {
      const data = await betaAPI.exportJSON();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `beta_feedback_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (error) {
      console.error('Failed to export JSON:', error);
    }
  }, []);

  const handleExportMarkdown = useCallback(async () => {
    try {
      const data = await betaAPI.exportMarkdown();
      const blob = new Blob([data.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `beta_feedback_${new Date().toISOString().split('T')[0]}.md`;
      a.click();
    } catch (error) {
      console.error('Failed to export Markdown:', error);
    }
  }, []);

  return {
    betaFeedback, betaStats, betaTrends, betaTesters, betaLoading,
    selectedFeedback, setSelectedFeedback,
    showDeleteFeedbackConfirm, setShowDeleteFeedbackConfirm,
    feedbackToDelete, setFeedbackToDelete,
    betaStatusFilter, setBetaStatusFilter,
    betaCategoryFilter, setBetaCategoryFilter,
    trendPeriod, setTrendPeriod,
    inviteCodes,
    showCreateCodeModal, setShowCreateCodeModal,
    showCreateTesterModal, setShowCreateTesterModal,
    newCodeData, setNewCodeData,
    betaSystemEnabled,
    adminResponseInput, setAdminResponseInput,
    respondingTo, setRespondingTo,
    newTesterData, setNewTesterData,
    codeLoading, testerLoading,
    setBetaTrends,
    fetchBetaData, fetchBetaSettings,
    handleToggleBetaSystem, handleAddAdminResponse,
    handleCreateCode, handleDeleteCode, handleToggleCode,
    handleCreateTester, handleDeleteTester, handleToggleTester,
    handleDeleteFeedback, handleUpdateFeedbackStatus,
    handleExportJSON, handleExportMarkdown,
  };
};
